import { readVenueListResponse } from '@liquid-spirit/api/domains/venues';
import { credentialScope } from '../utils/credentialScope.js';
import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from './AuthContext';
import { useCommunityContext } from './CommunityContext';
import {
  fetchVenues as apiFetchVenues,
  createVenue as apiCreateVenue,
  createCommunityVenue as apiCreateCommunityVenue,
  updateVenue as apiUpdateVenue,
  createVenueForUser as apiCreateVenueForUser,
} from '../services/VenueService';
import { fetchHomeBootSlice } from '../services/HomeBootService';
import queryKeys from '../queries/queryKeys';
import useQueryScope from '../queries/useQueryScope';
import { isAccessDenied } from '../queries/queryOwnership';

const defaultVenuesContext = {
  venues: [],
  loading: false,
  error: null,
  reload: async () => {},
  setVenues: () => {},
  createVenue: async () => null,
  createVenueForUser: async () => null,
  updateVenue: async () => null,
  upsertVenue: () => null,
};

const VenuesContext = createContext(defaultVenuesContext);

const getVenueId = (venue) => {
  const id = venue?._id || venue?.id || '';
  return id ? String(id) : '';
};

const sameVenueId = (left, right) => {
  const leftId = typeof left === 'object' ? getVenueId(left) : String(left || '');
  const rightId = typeof right === 'object' ? getVenueId(right) : String(right || '');
  return Boolean(leftId && rightId && leftId === rightId);
};

// An uninitialised query is empty; a populated cache must hold the service array.
const venueItems = (value) => {
  if (value === undefined) return [];
  return readVenueListResponse(value);
};

const withoutClientCoordinates = (value = {}) => {
  const payload = { ...value };
  delete payload.location;
  delete payload.lat;
  delete payload.lng;
  if (payload.address && typeof payload.address === 'object') {
    const address = { ...payload.address };
    delete address.lat;
    delete address.lng;
    payload.address = address;
  }
  return payload;
};

export const VenuesProvider = ({ children }) => {
  const auth = useAuthContext();
  const { community } = useCommunityContext();
  const communityId = community?._id;
  const token = auth?.token;
  const userId = auth?.user?._id || auth?.user?.id || auth?.user?.userId || null;
  const isAuthenticated = Boolean(token);
  const queryClient = useQueryClient();
  const { scope, isCurrent } = useQueryScope();
  const queryKey = useMemo(
    () => [...queryKeys.venues.byCommunity(communityId || 'none'), 'auth', credentialScope(token), scope],
    [communityId, token, scope],
  );

  const owner = `${communityId || 'none'}:${credentialScope(token)}`;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;

  const venuesQuery = useQuery({
    queryKey,
    enabled: Boolean(isAuthenticated && communityId && token),
    queryFn: ({ signal }) => fetchHomeBootSlice(
      'venues',
      { token, userId, communityId },
      () => apiFetchVenues(communityId, {
        signal, token,
        headers: { Authorization: `Bearer ${token}` },
      }),
    ),
    placeholderData: [],
    staleTime: 60_000,
  });

  const venues = useMemo(
    () => (isAuthenticated && !isAccessDenied(venuesQuery.error) ? venueItems(venuesQuery.data) : []),
    [isAuthenticated, venuesQuery.data, venuesQuery.error],
  );

  const setVenues = useCallback((itemsOrUpdater) => {
    if (!isCurrent() || ownerRef.current !== owner || !token) return;
    const current = venueItems(queryClient.getQueryData(queryKey));
    const resolvedItems = typeof itemsOrUpdater === 'function'
      ? itemsOrUpdater(current)
      : itemsOrUpdater;
    queryClient.setQueryData(queryKey, venueItems(resolvedItems));
  }, [queryClient, queryKey, owner, token, isCurrent]);

  const loadVenues = useCallback(async () => {
    if (!isAuthenticated || !communityId || !token) return [];
    return queryClient.fetchQuery({
      queryKey,
      staleTime: 0,
      queryFn: () => apiFetchVenues(communityId, {
        token,
        headers: { Authorization: `Bearer ${token}` },
      }),
    });
  }, [communityId, isAuthenticated, queryClient, queryKey, token]);

  const createVenue = useCallback(async (draft) => {
    const typeMap = {
      online: 'Online',
      residence: 'Residence',
      community: 'CommunityVenue',
    };
    const backendType = typeMap[draft.type] || draft.type;
    const payload = withoutClientCoordinates({ ...draft, type: backendType, community: communityId });

    if (!payload.name) {
      if (backendType === 'CommunityVenue') {
        throw new Error('Please provide a community venue name.');
      }
      if (backendType === 'Residence') {
        throw new Error('Please provide a residence name.');
      }
    }

    const apiCreate = backendType === 'CommunityVenue'
      ? apiCreateCommunityVenue
      : apiCreateVenue;
    const saved = await apiCreate(payload);
    const savedId = saved._id;
    setVenues(prev => [{ ...saved }, ...prev.filter(v => !sameVenueId(v, saved))]);
    const uiType = String(saved.type || '').toLowerCase() === 'communityvenue'
      ? 'community'
      : String(saved.type || '').toLowerCase();
    return {
      id: savedId,
      type: uiType,
      name: saved.name,
      onlineLink: saved.onlineLink,
      address: saved.address,
      household: saved.household,
      family: saved.family,
      community: saved.community,
    };
  }, [communityId, setVenues]);

  const upsertVenue = useCallback((nextVenue) => {
    if (!nextVenue) return nextVenue;
    const id = getVenueId(nextVenue);
    if (!id) return nextVenue;
    setVenues((prev) => [{ ...nextVenue }, ...prev.filter((venue) => !sameVenueId(venue, id))]);
    return nextVenue;
  }, [setVenues]);

  const createVenueForUser = useCallback(async (payload = {}) => {
    const writePayload = withoutClientCoordinates({
      ...payload,
      community: payload.community || communityId,
    });
    const saved = await apiCreateVenueForUser(writePayload);
    const id = saved._id;
    setVenues(prev => [{ ...saved }, ...prev.filter(v => !sameVenueId(v, id))]);
    const uiType = String(saved.type || '').toLowerCase() === 'communityvenue'
      ? 'community'
      : String(saved.type || '').toLowerCase();
    return {
      id,
      type: uiType,
      name: saved.name,
      onlineLink: saved.onlineLink,
      address: saved.address,
      availableForRequest: saved.availableForRequest,
      location: saved.location,
      household: saved.household,
      family: saved.family,
    };
  }, [communityId, setVenues]);

  const updateVenue = useCallback(async (id, updates) => {
    const normalized = withoutClientCoordinates(updates);
    const saved = await apiUpdateVenue(id, normalized);
    // The saved document is authoritative, including fields cleared by the server.
    setVenues(prev => prev.map(v => sameVenueId(v, saved) ? saved : v));
    return saved;
  }, [setVenues]);

  const value = useMemo(
    () => ({
      venues,
      loading: venuesQuery.isLoading,
      error: venuesQuery.error?.message || null,
      reload: loadVenues,
      setVenues,
      createVenue,
      createVenueForUser,
      updateVenue,
      upsertVenue,
    }),
    [
      createVenue,
      createVenueForUser,
      loadVenues,
      setVenues,
      updateVenue,
      upsertVenue,
      venues,
      venuesQuery.error?.message,
      venuesQuery.isLoading,
    ],
  );
  return <VenuesContext.Provider value={value}>{children}</VenuesContext.Provider>;
};

export const useVenuesContext = () => useContext(VenuesContext);
